import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const CriticalAlertsPanel = ({ isOpen, onClose, className = '' }) => {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('severity');

  useEffect(() => {
    // Simulate real-time alerts
    const mockAlerts = [
      {
        id: 1,
        type: 'equipment',
        severity: 'high',
        title: 'Calibration Overdue',
        message: 'HPLC System #3 calibration expired 2 days ago',
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        source: 'Laboratory Equipment',
        actionRequired: true,
        acknowledged: false
      },
      {
        id: 2,
        type: 'quality',
        severity: 'high',
        title: 'Failed Batch Test',
        message: 'Batch #QA-2024-1247 failed purity test - requires immediate review',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        source: 'Batch Testing',
        actionRequired: true,
        acknowledged: false
      },
      {
        id: 3,
        type: 'regulatory',
        severity: 'medium',
        title: 'Submission Deadline',
        message: 'FDA submission for Product XYZ due in 5 days',
        timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
        source: 'Regulatory Compliance',
        actionRequired: false,
        acknowledged: false
      },
      {
        id: 4,
        type: 'safety',
        severity: 'medium',
        title: 'Adverse Event Update',
        message: 'New adverse event report requires classification',
        timestamp: new Date(Date.now() - 30 * 60 * 1000),
        source: 'Safety Reporting',
        actionRequired: true,
        acknowledged: false
      },
      {
        id: 5,
        type: 'blockchain',
        severity: 'low',
        title: 'Network Latency',
        message: 'Blockchain network experiencing higher than normal latency',
        timestamp: new Date(Date.now() - 15 * 60 * 1000),
        source: 'Blockchain Network',
        actionRequired: false,
        acknowledged: true
      }
    ];

    setAlerts(mockAlerts);
  }, []);

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'high':
        return {
          color: 'text-error',
          bgColor: 'bg-error/5',
          borderColor: 'border-l-error',
          icon: 'AlertTriangle'
        };
      case 'medium':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/5',
          borderColor: 'border-l-warning',
          icon: 'AlertCircle'
        };
      case 'low':
        return {
          color: 'text-primary',
          bgColor: 'bg-primary/5',
          borderColor: 'border-l-primary',
          icon: 'Info'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          borderColor: 'border-l-muted',
          icon: 'Circle'
        };
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'equipment': return 'Settings';
      case 'quality': return 'FlaskConical';
      case 'regulatory': return 'CheckCircle';
      case 'safety': return 'Shield';
      case 'blockchain': return 'Blocks';
      default: return 'Bell';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const filteredAlerts = alerts?.filter(alert => {
      if (filter === 'all') return true;
      if (filter === 'unacknowledged') return !alert?.acknowledged;
      if (filter === 'actionRequired') return alert?.actionRequired;
      return alert?.severity === filter;
    })?.sort((a, b) => {
      if (sortBy === 'severity') {
        const severityOrder = { high: 3, medium: 2, low: 1 };
        return severityOrder?.[b?.severity] - severityOrder?.[a?.severity];
      }
      if (sortBy === 'time') {
        return b?.timestamp - a?.timestamp;
      }
      return 0;
    });

  const handleAcknowledge = (alertId) => {
    setAlerts(prev => prev?.map(alert => 
      alert?.id === alertId ? { ...alert, acknowledged: true } : alert
    ));
  };

  const handleDismiss = (alertId) => {
    setAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 ${className}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-popover border-l border-border shadow-modal animate-slide-down">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Bell" size={20} className="text-foreground" />
            <h2 className="text-lg font-semibold text-popover-foreground">Critical Alerts</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-popover-foreground">Filter:</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e?.target?.value)}
              className="text-sm border border-border rounded-md px-2 py-1 bg-input text-foreground"
            >
              <option value="all">All Alerts</option>
              <option value="unacknowledged">Unacknowledged</option>
              <option value="actionRequired">Action Required</option>
              <option value="high">High Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="low">Low Priority</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-popover-foreground">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="text-sm border border-border rounded-md px-2 py-1 bg-input text-foreground"
            >
              <option value="severity">Severity</option>
              <option value="time">Time</option>
            </select>
          </div>
        </div>

        {/* Alerts List */}
        <div className="flex-1 overflow-y-auto">
          {filteredAlerts?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
              <p className="text-muted-foreground">No alerts match your filters</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredAlerts?.map((alert) => {
                const severityConfig = getSeverityConfig(alert?.severity);
                return (
                  <div
                    key={alert?.id}
                    className={`p-4 border-l-4 ${severityConfig?.borderColor} ${severityConfig?.bgColor} ${
                      alert?.acknowledged ? 'opacity-60' : ''
                    } hover:bg-muted/30 transition-colors duration-150`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Icon 
                          name={getTypeIcon(alert?.type)} 
                          size={16} 
                          className={severityConfig?.color}
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="text-sm font-medium text-popover-foreground truncate">
                            {alert?.title}
                          </h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${severityConfig?.bgColor} ${severityConfig?.color} font-medium uppercase`}>
                            {alert?.severity}
                          </span>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-2">
                          {alert?.message}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{alert?.source}</span>
                          <span>{formatTimeAgo(alert?.timestamp)}</span>
                        </div>
                        
                        {!alert?.acknowledged && (
                          <div className="flex items-center space-x-2 mt-3">
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={() => handleAcknowledge(alert?.id)}
                            >
                              Acknowledge
                            </Button>
                            {alert?.actionRequired && (
                              <Button
                                variant="default"
                                size="xs"
                              >
                                Take Action
                              </Button>
                            )}
                            <button
                              onClick={() => handleDismiss(alert?.id)}
                              className="text-muted-foreground hover:text-foreground transition-colors duration-150"
                            >
                              <Icon name="X" size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <Button variant="outline" className="w-full">
            View All Alerts
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CriticalAlertsPanel;